import { getParamsDelimiter } from '@aibees/elephant/feature/search-filters/components/filter-tags/utils';
import { buildDraft } from '@aibees/elephant/feature/search-filters/filters/number-range-filter/utils';
import { SearchFilterType } from '@aibees/elephant/feature/search-filters/types';
import { useSearchQueryFilter } from '@aibees/elephant/hooks';
import { Chip, Icon, TextEllipsis } from '@aibees/shared/components';
import {
  classnames,
  decodeQueryParamValues,
  trimText,
} from '@aibees/shared/utils';
import { Box, Grid } from '@mui/material';
import { memo, useCallback } from 'react';
import * as SX from './sx';
import type { FilterTagProps as Props } from './types';

export const FilterTag = memo(
  ({ className, data, info, field, disabledClear, removable }: Props) => {
    const { color, icon, type } = info;
    const label = data?.label ?? '';
    const trimmedLabel = trimText(label, 150);

    const { setParam, getParam, removeParam, params } = useSearchQueryFilter({
      select: field ? [field] : [],
    });

    const handleDelete = useCallback(() => {
      if (!data?.value) return;
      if (type === SearchFilterType.BULK) return removeParam(field!);

      const index = decodeQueryParamValues(
        getParam(field!) ?? undefined,
        getParamsDelimiter(type)
      ).indexOf(data.value);

      const action = data.value === getParam(field!) ? 'clear' : 'remove';
      const draft = buildDraft(params, type, field, action, index);
      setParam(field, draft);
    }, [data, field, getParam, params, removeParam, setParam, type]);

    return (
      <Grid
        display={'flex'}
        alignItems={'center'}
        gap={0.5}
        sx={SX.root(disabledClear)}
      >
        <Chip
          className={classnames('FilterTag', className)}
          variant="outlined"
          size="xsmall"
          label={
            <Box display={'flex'} alignItems={'center'}>
              <TextEllipsis
                variant="body5"
                color="grey.700"
                value={trimmedLabel}
                w="200px"
              />
            </Box>
          }
          onDelete={!disabledClear || removable ? handleDelete : undefined}
          color={color}
          deleteIcon={
            !disabledClear || removable ? (
              <Icon size={10} color="common.black" name="close" />
            ) : undefined
          }
          icon={icon ? <Icon size={14} name={icon} color={color} /> : undefined}
        />
      </Grid>
    );
  }
);

FilterTag.displayName = 'FilterTag';
